import type {FolderDispatcher} from '@gaia/hooks/useFolders';
import createAsyncMutex from '@gaia/utils/createAsyncMutex';
import debounceWithResolver from '@gaia/utils/debounceWithResolver';
import fetchJSON from '@gaia/utils/fetchJSON';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

const debouncedFetch = debounceWithResolver(window.fetch, 500);

const savedFolders: Record<string, Folder> = {};

export function getIsFolderModifiedSinceSave(folder: Folder) {
    const savedFolder = savedFolders[folder.localId];

    if (!savedFolder && folder.deleted) return false;
    else if (!savedFolder) return true;
    else if (
        isEqual(
            omit(savedFolder, [
                'id',
                'waypoints',
                'tracks',
                'areas',
                'routes',
                'folders',
                'maps',
                'mapSources',
            ]),
            omit(folder, [
                'id',
                'waypoints',
                'tracks',
                'areas',
                'routes',
                'folders',
                'maps',
                'mapSources',
            ])
        )
    )
        return false;
    else if (savedFolder.deleted) return false;
    else return true;
}

export function registerSavedFolder(folder: Folder) {
    savedFolders[folder.localId!] = folder;
}

export default createAsyncMutex(
    async (folder: Folder, folderDispatcher: FolderDispatcher): Promise<void> => {
        const isModifiedSinceSave = getIsFolderModifiedSinceSave(folder);
        if (!isModifiedSinceSave) return;
        registerSavedFolder(folder);

        const body = JSON.stringify({
            ...omit(folder, [
                'localId',
                'id',
                'type',
                'waypoints',
                'tracks',
                'areas',
                'routes',
                'folders',
                'maps',
                'mapSources',
            ]),
        });

        if (folder.id) {
            await debouncedFetch(`/api/objects/folder/${folder.id}/`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
        } else {
            const response = await fetchJSON('/api/objects/folder/', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
            const id = response.properties.id;
            folderDispatcher({
                type: 'updateProperties',
                properties: {
                    id,
                    localId: folder.localId!,
                },
            });
        }
    },
    (folder) => folder.localId!
);
